import React, { useState, useMemo } from 'react';
import { Card, Table, Spinner, Pagination, Dropdown, Button, Form, ButtonGroup } from 'react-bootstrap';
import { FaInbox, FaSort, FaSortUp, FaSortDown, FaFileExcel, FaFileCsv, FaFilePdf, FaPrint, FaSearch, FaFilter } from 'react-icons/fa';
import { BrandTheme } from '../../theme/BrandTheme';
import { exportToExcel, exportToCSV, exportToPDF } from '../../../../utils/exportUtils';
import TableFilter from './TableFilter';

/**
 * Enhanced Reusable BrandTable Component
 * Comprehensive table with pagination, selection, sorting, export, and print
 */
const BrandTable = ({
    // Core Data
    data = [],
    columns = [],
    loading = false,
    loadingMessage = 'Loading data...',
    idKey = '_id',

    // Display
    title,
    className = '',
    striped = true,
    hover = true,
    bordered = false,
    size = 'md',

    // Selection
    selectable = false,
    selectedRows = [],
    onSelectionChange,
    selectAll = false,

    // Pagination
    pagination = false,
    currentPage = 1,
    totalRecords,
    rowsPerPage = 10,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = [5, 10, 20, 50, 100],

    // Sorting
    sortable = false,
    sortConfig = { key: '', direction: 'asc' },
    onSort,

    // Toolbar & Search
    toolbar,
    searchable = false,
    searchPlaceholder = 'Search...',
    onSearch,
    disableInternalSearch = false,

    // Row Actions
    actions = [],

    // Export
    exportable = false,
    exportFormats = ['excel', 'csv', 'pdf'],
    exportFileName = 'export',
    onExport,

    // Print
    printable = false,
    onPrint,

    // Filters
    enableFilters = false,
    filterProps = {}, // { filters, onFilterChange, onClear, filterOptions }

    // Expandable
    expandable = false,
    renderExpandedRow,
    expandedRows: controlledExpandedRows,
    onExpandedRowsChange,

    // Server-side mode
    serverSide = false,
    onFetchData,

    // Empty State
    emptyMessage = 'No records found',
    emptyIcon = <FaInbox size={40} className="mb-3 text-secondary opacity-50" />,
    emptyAction,

    // Row Customization
    rowClassName,
    onRowClick,
    onCellClick,
}) => {
    // Use controlled expandedRows if provided, otherwise use internal state
    const [internalExpandedRows, setInternalExpandedRows] = useState([]);
    const expandedRows = controlledExpandedRows !== undefined ? controlledExpandedRows : internalExpandedRows;
    const setExpandedRows = onExpandedRowsChange || setInternalExpandedRows;
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Handle "Select All"
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = data.map(row => row[idKey]);
            onSelectionChange?.(allIds);
        } else {
            onSelectionChange?.([]);
        }
    };

    // Handle individual row selection
    const handleRowSelect = (rowId, checked) => {
        const newSelection = checked
            ? [...selectedRows, rowId]
            : selectedRows.filter(id => id !== rowId);
        onSelectionChange?.(newSelection);
    };

    // Sorting State
    const [internalSortConfig, setInternalSortConfig] = useState(sortConfig);
    const effectiveSortConfig = onSort ? sortConfig : internalSortConfig;

    // Handle sorting
    const handleSort = (columnKey) => {
        if (!sortable) return;

        const newDirection = effectiveSortConfig.key === columnKey && effectiveSortConfig.direction === 'asc' ? 'desc' : 'asc';
        const newConfig = { key: columnKey, direction: newDirection };

        if (onSort) {
            onSort(columnKey, newDirection);
        } else {
            setInternalSortConfig(newConfig);
        }
    };

    // Render sort icon
    const renderSortIcon = (columnKey) => {
        if (!sortable) return null;
        if (effectiveSortConfig.key !== columnKey) return <FaSort className="ms-1 text-muted" />;
        return effectiveSortConfig.direction === 'asc'
            ? <FaSortUp className="ms-1" style={{ color: BrandTheme.colors.primary }} />
            : <FaSortDown className="ms-1" style={{ color: BrandTheme.colors.primary }} />;
    };

    // Valid filter check helper
    // Helper: Match search term against item properties
    const matchesSearch = (item, term) => {
        const lowerTerm = term.toLowerCase();

        const searchInObj = (obj) => {
            if (!obj) return false;

            // Check direct primitive values
            for (const key in obj) {
                const val = obj[key];

                // Specific high-priority fields for deep search
                if (['referenceNumber', 'supplier_bill_no', 'batch_number', 'serialNumber'].includes(key)) {
                    if (String(val).toLowerCase().includes(lowerTerm)) return true;
                }

                if (typeof val === 'string' || typeof val === 'number') {
                    if (String(val).toLowerCase().includes(lowerTerm)) return true;
                } else if (Array.isArray(val)) {
                    // Search in arrays (e.g., serializedItems)
                    if (val.some(v => typeof v === 'object' ? searchInObj(v) : String(v).toLowerCase().includes(lowerTerm))) return true;
                } else if (typeof val === 'object' && val !== null) {
                    // Search in objects (e.g., supplier, items)
                    // We only go one level deep for general objects to avoid infinite loops/performance issues
                    // but we look for common names
                    const subVal = val.business_name || val.first_name || val.name || val.title;
                    if (subVal && String(subVal).toLowerCase().includes(lowerTerm)) return true;
                }
            }
            return false;
        };

        return searchInObj(item);
    };
    const filteredData = useMemo(() => {
        let result = data;

        // 1. Filter
        if (!serverSide && searchQuery && !disableInternalSearch) {
            result = result.filter(item => matchesSearch(item, searchQuery));
        }

        // 2. Sort (Client-Side)
        if (!serverSide && effectiveSortConfig.key) {
            result = [...result].sort((a, b) => {
                let valA = a[effectiveSortConfig.key];
                let valB = b[effectiveSortConfig.key];

                // Deep resolution helper (handles supplier/customer objects if key matches object)
                const resolveValue = (val) => {
                    if (val === null || val === undefined) return '';
                    if (typeof val === 'object') {
                        // Attempt to extract common logical names if the row is a populated object
                        return val.business_name || val.first_name || val.name || val.title || JSON.stringify(val);
                    }
                    return val;
                };

                valA = resolveValue(valA);
                valB = resolveValue(valB);

                // String comparison (case-insensitive)
                if (typeof valA === 'string' && typeof valB === 'string') {
                    // Try to parse as date strictly if it looks like an ISO date string
                    const dateA = Date.parse(valA);
                    const dateB = Date.parse(valB);
                    if (!isNaN(dateA) && !isNaN(dateB) && valA.includes('-') && valB.includes('-')) {
                        return effectiveSortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
                    }

                    return effectiveSortConfig.direction === 'asc'
                        ? valA.localeCompare(valB)
                        : valB.localeCompare(valA);
                }

                // Number comparison
                if (valA < valB) return effectiveSortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return effectiveSortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [data, searchQuery, serverSide, effectiveSortConfig]);

    // Calculate pagination
    const effectiveTotalRecords = serverSide ? totalRecords : filteredData.length;
    const totalPages = Math.ceil(effectiveTotalRecords / rowsPerPage);
    const isAllSelected = data.length > 0 && selectedRows.length === data.length;
    const isSomeSelected = selectedRows.length > 0 && selectedRows.length < data.length;

    // Export handlers
    const handleExport = (format) => {
        if (onExport) {
            onExport(format, data);
        } else {
            // Default export logic using lazy-loaded utilities
            const fileName = exportFileName || title || 'export';
            switch (format) {
                case 'excel':
                    exportToExcel(data, fileName, columns);
                    break;
                case 'csv':
                    exportToCSV(data, fileName, columns);
                    break;
                case 'pdf':
                    exportToPDF(data, fileName, columns);
                    break;
                default:
                    console.warn(`Export format ${format} not supported.`);
            }
        }
    };

    // Print handler
    const handlePrint = () => {
        if (onPrint) {
            onPrint(data);
        } else {
            window.print();
        }
    };

    // Toggle row expansion
    const toggleRowExpansion = (rowId) => {
        setExpandedRows(prev =>
            prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
        );
    };

    // Filtered data (Search)


    // Local pagination logic
    const paginatedData = useMemo(() => {
        if (serverSide || !pagination) return filteredData; // [FIX] Return all data if pagination is false
        const startIndex = (currentPage - 1) * rowsPerPage;
        return filteredData.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredData, serverSide, pagination, currentPage, rowsPerPage]);

    // Reset page on search
    React.useEffect(() => {
        if (!serverSide && onPageChange) {
            onPageChange(1);
        }
    }, [searchQuery]);

    return (
        <Card className={`border-0 shadow-sm ${className}`} style={{ borderRadius: BrandTheme.borderRadius.card }}>
            {/* Header */}
            {(title || exportable || printable || enableFilters) && (
                <Card.Header className="bg-transparent border-bottom px-3 px-md-4 py-3 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                    {title && <h6 className="mb-0 fw-bold">{title}</h6>}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {exportable && (
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm" className="shadow-sm">
                                    <FaFileCsv className="me-1" /> Export
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="shadow border-0">
                                    {exportFormats.includes('excel') && (
                                        <Dropdown.Item onClick={() => handleExport('excel')}>
                                            <FaFileExcel className="me-2 text-success" /> Excel
                                        </Dropdown.Item>
                                    )}
                                    {exportFormats.includes('csv') && (
                                        <Dropdown.Item onClick={() => handleExport('csv')}>
                                            <FaFileCsv className="me-2 text-info" /> CSV
                                        </Dropdown.Item>
                                    )}
                                    {exportFormats.includes('pdf') && (
                                        <Dropdown.Item onClick={() => handleExport('pdf')}>
                                            <FaFilePdf className="me-2 text-danger" /> PDF
                                        </Dropdown.Item>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                        {printable && (
                            <Button variant="outline-secondary" size="sm" onClick={handlePrint} className="shadow-sm">
                                <FaPrint className="me-1" /> Print
                            </Button>
                        )}
                        {enableFilters && (
                            <Button
                                variant={showFilters ? "primary" : "outline-primary"}
                                size="sm"
                                onClick={() => setShowFilters(!showFilters)}
                                className="shadow-sm"
                            >
                                <FaFilter className="me-1" /> Filter
                            </Button>
                        )}
                    </div>
                </Card.Header>
            )}

            {/* Advanced Filters */}
            {enableFilters && (
                <TableFilter
                    show={showFilters}
                    onHide={() => setShowFilters(false)}
                    {...filterProps}
                />
            )}

            {/* Toolbar (Custom Filters) */}
            {toolbar && (
                <div className="bg-light border-bottom p-0">
                    {toolbar}
                </div>
            )}

            {/* Search Bar */}
            {searchable && (
                <div className="bg-light bg-opacity-50 border-bottom p-2 p-md-3">
                    <div className="input-group input-group-sm bg-white rounded-3 border shadow-2xs overflow-hidden">
                        <span className="input-group-text bg-transparent border-0 text-muted ps-2.5 pe-1.5">
                            <FaSearch size={12} />
                        </span>
                        <Form.Control
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchQuery}
                            className="border-0 shadow-none ps-1 py-1.5 text-uppercase"
                            style={{ fontSize: '0.8rem', letterSpacing: '0.3px' }}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (window.brandTableSearchTimeout) {
                                    clearTimeout(window.brandTableSearchTimeout);
                                }
                                window.brandTableSearchTimeout = setTimeout(() => {
                                    onSearch?.(e.target.value);
                                }, 300);
                            }}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-muted text-decoration-none pe-2.5"
                                style={{ fontSize: '1rem', lineHeight: 1 }}
                                onClick={() => {
                                    setSearchQuery("");
                                    onSearch?.("");
                                }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>
            )}

            <Card.Body className="p-0">
                <div className="table-responsive" style={{ minHeight: 'auto', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table
                        hover={hover}
                        striped={striped}
                        bordered={bordered}
                        size={size}
                        className="mb-0 align-middle"
                        style={{ tableLayout: 'auto' }}
                    >
                        <thead className="bg-light text-uppercase small" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                {/* Select All Checkbox */}
                                {selectable && (
                                    <th className="ps-4" style={{ width: '45px' }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={isAllSelected}
                                            className="cursor-pointer"
                                            ref={input => input && (input.indeterminate = isSomeSelected)}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                )}

                                {/* Column Headers */}
                                {columns.map((col, idx) => (
                                    <th
                                        key={idx}
                                        className={`${idx === 0 && !selectable ? 'ps-4' : ''} ${idx === columns.length - 1 ? 'pe-4' : ''} ${col.headerClassName || ''} ${col.sortable ? 'cursor-pointer user-select-none' : ''} py-3 text-muted fw-bold`}
                                        style={{ width: col.width, textAlign: col.align || 'left', fontSize: '0.75rem', letterSpacing: '0.5px' }}
                                        onClick={() => col.sortable && handleSort(col.key)}
                                    >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span>{col.label}</span>
                                            {col.sortable && renderSortIcon(col.key)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Loading State */}
                            {loading && (
                                <tr>
                                    <td colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)} className="text-center py-5">
                                        <Spinner animation="border" variant="primary" role="status" size="sm" />
                                        <p className="mt-2 text-muted x-small">{loadingMessage}</p>
                                    </td>
                                </tr>
                            )}

                            {/* Empty State */}
                            {!loading && paginatedData.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)} className="text-center py-5 text-muted">
                                        <div className="animate__animated animate__fadeIn">
                                            {emptyIcon}
                                            <h6 className="fw-bold">{searchQuery ? `No results found for "${searchQuery}"` : emptyMessage}</h6>
                                            {searchQuery ? (
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mt-3 rounded-pill px-4 shadow-sm"
                                                    onClick={() => {
                                                        setSearchQuery("");
                                                        onSearch?.("");
                                                    }}
                                                >
                                                    Reset Search
                                                </Button>
                                            ) : emptyAction && (
                                                <Button variant="primary" size="sm" className="mt-2" onClick={emptyAction.onClick}>
                                                    {emptyAction.label}
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {/* Data Rows */}
                            {!loading && paginatedData.map((row, rowIndex) => {
                                const rowId = row[idKey];
                                const isExpanded = expandedRows.includes(rowId);
                                const isSelected = selectedRows.includes(rowId);
                                const customRowClass = rowClassName ? rowClassName(row, rowIndex) : '';

                                return (
                                    <React.Fragment key={rowId || rowIndex}>
                                        <tr
                                            className={`${customRowClass} ${isSelected ? 'table-primary shadow-sm' : ''}`}
                                            onClick={() => onRowClick?.(row)}
                                            style={{ cursor: onRowClick ? 'pointer' : 'default', borderLeft: isSelected ? `4px solid ${BrandTheme.colors.primary}` : 'none', fontSize: '14px !important' }}
                                        >
                                            {/* Selection Checkbox */}
                                            {selectable && (
                                                <td className="ps-4" style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => handleRowSelect(rowId, e.target.checked)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                            )}

                                            {/* Data Cells */}
                                            {columns.map((col, colIndex) => (
                                                <td
                                                    key={colIndex}
                                                    className={`${colIndex === 0 && !selectable ? 'ps-4' : ''} ${colIndex === columns.length - 1 && actions.length === 0 ? 'pe-4' : ''} ${col.className || ''}`}
                                                    style={{
                                                        textAlign: col.align || 'left',
                                                        padding: '14px 16px',
                                                        verticalAlign: 'middle',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        color: '#000000 !important',
                                                        fontSize: '14px !important',
                                                        fontWeight: '500'
                                                    }}
                                                    onClick={() => onCellClick?.(row, col.key)}
                                                >
                                                    {col.render ? col.render(row[col.key], row, rowIndex) : row[col.key]}
                                                </td>
                                            ))}

                                            {/* Actions */}
                                            {actions.length > 0 && (
                                                <td className="pe-4 text-end" style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                                    <div className="d-flex justify-content-end gap-2">
                                                        {actions.map((action, idx) => {
                                                            const shouldShow = action.show ? action.show(row) : true;
                                                            if (!shouldShow) return null;

                                                            return (
                                                                <Button
                                                                    key={idx}
                                                                    variant={action.variant || 'secondary'}
                                                                    size="sm"
                                                                    className={`d-flex align-items-center gap-1 ${action.className || ''}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        action.onClick?.(row);
                                                                    }}
                                                                    title={action.label}
                                                                >
                                                                    {action.icon}
                                                                    {!action.iconOnly && <span className="d-none d-md-inline">{action.label}</span>}
                                                                </Button>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>

                                        {/* Expandable Row */}
                                        {expandable && isExpanded && (
                                            <tr>
                                                <td colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)} className="bg-light">
                                                    {renderExpandedRow?.(row)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>

            {/* Pagination */}
            {pagination && (effectiveTotalRecords > 0) && (
                <Card.Footer className="bg-white border-top py-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                        {/* Rows Per Page Selector */}
                        <div className="d-flex align-items-center gap-2">
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm" className="rounded-pill">
                                    Rows: {rowsPerPage}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {rowsPerPageOptions.map(option => (
                                        <Dropdown.Item
                                            key={option}
                                            active={rowsPerPage === option}
                                            onClick={() => onRowsPerPageChange?.(option)}
                                        >
                                            {option}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            <small className="text-muted d-md-none">
                                {currentPage} / {totalPages} ({effectiveTotalRecords} items)
                            </small>
                        </div>

                        {/* Pagination Controls */}
                        <div className="overflow-auto max-w-100 d-flex justify-content-center">
                            <Pagination className="mb-0 flex-nowrap shadow-2xs">
                                <Pagination.First onClick={() => onPageChange?.(1)} disabled={currentPage === 1} />
                                <Pagination.Prev onClick={() => onPageChange?.(currentPage - 1)} disabled={currentPage === 1} />

                                {[...Array(totalPages).keys()].map(pageNum => {
                                    const page = pageNum + 1;
                                    // Show first, last, current, and adjacent pages
                                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                        return (
                                            <Pagination.Item
                                                key={page}
                                                active={page === currentPage}
                                                onClick={() => onPageChange?.(page)}
                                            >
                                                {page}
                                            </Pagination.Item>
                                        );
                                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                                        return <Pagination.Ellipsis key={page} disabled />;
                                    }
                                    return null;
                                })}

                                <Pagination.Next onClick={() => onPageChange?.(currentPage + 1)} disabled={currentPage === totalPages} />
                                <Pagination.Last onClick={() => onPageChange?.(totalPages)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>

                        {/* Info */}
                        <small className="text-muted d-none d-md-block">
                            Page {currentPage} of {totalPages} | {effectiveTotalRecords} records total
                        </small>
                    </div>
                </Card.Footer>
            )}
        </Card>
    );
};

export default BrandTable;
